import isEqual from "lodash/isEqual";

import { normalizeSlackChannel } from "@/common/utils/slack";
import { AppDTO } from "@reflag/shared/appAPI";
import { FeatureDetail } from "@reflag/shared/featureAPI";
import { UpdateEnvironmentFeatureTargeting } from "@reflag/shared/featureTargetingAPI";
import { NotificationProvider } from "@reflag/shared/notification";
import {
  defaultTargetingNotificationPolicies,
  policyAllowsTargetingNotification,
} from "@reflag/shared/targetingNotificationPolicy";
import {
  LinearNotificationSettings,
  SlackNotificationSettings,
  TargetingNotificationPolicies,
} from "@reflag/shared/targetingNotificationPolicyAPI";

export type TargetingNotificationSettings = {
  slack: Pick<SlackNotificationSettings, "policy" | "environments">;
  linear: Pick<LinearNotificationSettings, "policy" | "environments">;
};

function getChangedEnvironmentIds(
  currentTargeting: UpdateEnvironmentFeatureTargeting[],
  newTargeting: UpdateEnvironmentFeatureTargeting[],
) {
  return new Set(
    newTargeting
      .filter((newVersion) => {
        const oldVersion = currentTargeting.find(
          ({ environmentId }) => environmentId === newVersion.environmentId,
        );

        if (!oldVersion) {
          return true;
        }

        return !isEqual(
          {
            targetingMode: oldVersion.targetingMode,
            companyIds: oldVersion.companyIds,
            segmentIds: oldVersion.segmentIds,
            userIds: oldVersion.userIds,
            customRules: oldVersion.customRules,
            environmentId: oldVersion.environmentId,
          },
          {
            targetingMode: newVersion.targetingMode,
            companyIds: newVersion.companyIds,
            segmentIds: newVersion.segmentIds,
            userIds: newVersion.userIds,
            customRules: newVersion.customRules,
            environmentId: newVersion.environmentId,
          },
        );
      })
      .map(({ environmentId }) => environmentId),
  );
}

function getEnabledEnvironmentIds(
  app: AppDTO,
  environmentSettings:
    | Pick<SlackNotificationSettings, "environments">
    | Pick<LinearNotificationSettings, "environments">
    | undefined,
) {
  const enabledByEnvironmentId = new Map(
    environmentSettings?.environments.map((environment) => [
      environment.environmentId,
      environment.enabled,
    ]) ?? [],
  );

  return new Set(
    app.environments
      .filter(
        (environment) =>
          enabledByEnvironmentId.get(environment.id) ??
          environment.isProduction,
      )
      .map((environment) => environment.id),
  );
}

export function getAvailableNotificationProvidersForFeature({
  app,
  feature,
  orgIntegrations,
}: {
  app: AppDTO;
  feature: FeatureDetail;
  orgIntegrations: {
    slack: boolean;
    linear: boolean;
  };
}): NotificationProvider[] {
  const appSlackChannel = normalizeSlackChannel(
    app.integrations?.slack?.channel ?? undefined,
  );

  const hasSlackDestination =
    orgIntegrations.slack &&
    !!(feature.integrations.slack.slackChannel || appSlackChannel);

  const hasLinearDestination =
    orgIntegrations.linear && !!feature.integrations.linear?.trackingEntity;

  return [
    ...(hasSlackDestination ? (["slack"] as const) : []),
    ...(hasLinearDestination ? (["linearComment"] as const) : []),
  ];
}

export function getDefaultNotificationProvidersForTargetingChange({
  app,
  feature,
  currentTargeting,
  newTargeting,
  currentStageId,
  newStageId,
  orgIntegrations,
  policies,
  notificationSettings,
}: {
  app: AppDTO;
  feature: FeatureDetail;
  currentTargeting: UpdateEnvironmentFeatureTargeting[];
  newTargeting: UpdateEnvironmentFeatureTargeting[];
  currentStageId: string | null;
  newStageId: string | null;
  orgIntegrations: {
    slack: boolean;
    linear: boolean;
  };
  policies?: TargetingNotificationPolicies;
  notificationSettings?: TargetingNotificationSettings;
}): NotificationProvider[] {
  return getTargetingNotificationDefaultsInfo({
    app,
    feature,
    currentTargeting,
    newTargeting,
    currentStageId,
    newStageId,
    orgIntegrations,
    policies,
    notificationSettings,
  }).defaultProviders;
}

export type TargetingNotificationDefaultsInfo = {
  stageChanged: boolean;
  effectivePolicies: TargetingNotificationPolicies;
  availableProviders: NotificationProvider[];
  defaultProviders: NotificationProvider[];
};

export function getTargetingNotificationDefaultsInfo({
  app,
  feature,
  currentTargeting,
  newTargeting,
  currentStageId,
  newStageId,
  orgIntegrations,
  policies,
  notificationSettings,
}: {
  app: AppDTO;
  feature: FeatureDetail;
  currentTargeting: UpdateEnvironmentFeatureTargeting[];
  newTargeting: UpdateEnvironmentFeatureTargeting[];
  currentStageId: string | null;
  newStageId: string | null;
  orgIntegrations: {
    slack: boolean;
    linear: boolean;
  };
  policies?: TargetingNotificationPolicies;
  notificationSettings?: TargetingNotificationSettings;
}): TargetingNotificationDefaultsInfo {
  const stageChanged = (currentStageId ?? null) !== (newStageId ?? null);
  const changedEnvironmentIds = getChangedEnvironmentIds(
    currentTargeting,
    newTargeting,
  );

  const defaultPolicies = defaultTargetingNotificationPolicies();
  const effectivePolicies = notificationSettings
    ? ({
        slack: notificationSettings.slack.policy,
        linear: notificationSettings.linear.policy,
      } satisfies TargetingNotificationPolicies)
    : (policies ??
      ({
        slack: defaultPolicies.slack,
        linear: defaultPolicies.linearComment,
      } satisfies TargetingNotificationPolicies));

  const effectivePoliciesByProvider = {
    slack: effectivePolicies.slack,
    linearComment: effectivePolicies.linear,
  } as const;

  const enabledEnvironmentIdsByProvider = {
    slack: getEnabledEnvironmentIds(app, notificationSettings?.slack),
    linearComment: getEnabledEnvironmentIds(app, notificationSettings?.linear),
  } as const;

  const availableProviders = getAvailableNotificationProvidersForFeature({
    app,
    feature,
    orgIntegrations,
  });

  const defaultProviders = availableProviders.filter(
    (provider) =>
      policyAllowsTargetingNotification(
        effectivePoliciesByProvider[provider],
        stageChanged,
      ) &&
      (stageChanged ||
        [...enabledEnvironmentIdsByProvider[provider]].some((environmentId) =>
          changedEnvironmentIds.has(environmentId),
        )),
  );

  return {
    stageChanged,
    effectivePolicies,
    availableProviders,
    defaultProviders,
  };
}
